<template>
    <main class="min-h-screen bg-background ">
        <UnderwaterBg class="fixed inset-0 z-0" />
        <div class="relative z-10 flex min-h-screen items-center justify-center">
            <div class="w-full max-w-md space-y-8 rounded-xl bg-white/80 p-8 shadow-lg text-center">
                <div v-if="loading" class="flex justify-center">
                    <v-progress-circular indeterminate />
                </div>
                <div v-else>
                    <v-icon
                        :color="status === 'success' ? 'success' : 'error'"
                        size="64"
                        class="mb-4"
                    >
                        {{ status === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                    <h2 class="text-2xl font-bold mb-4">{{ title }}</h2>
                    <p class="mb-6">{{ message }}</p>
                    
                    <!-- Add resend button for expired tokens -->
                    <div v-if="status === 'error' && isExpired" class="mb-6">
                        <!-- Email input if not available from URL -->
                        <div v-if="!userEmail" class="mb-4">
                            <v-text-field
                                v-model="emailInput"
                                label="Email Address"
                                type="email"
                                variant="outlined"
                                :rules="[
                                    v => !!v || 'Email is required',
                                    v => /.+@.+\..+/.test(v) || 'Email must be valid'
                                ]"
                                class="mb-2"
                            />
                        </div>
                        
                        <v-btn
                            color="primary"
                            variant="elevated"
                            :loading="resending"
                            :disabled="resending || (!userEmail && !emailInput?.trim())"
                            @click="resendConfirmation"
                            class="mb-3"
                            block
                        >
                            <v-icon class="mr-2">mdi-email-send</v-icon>
                            Resend Confirmation Email
                        </v-btn>
                        <p class="text-sm text-gray-600">
                            Didn't receive the email? Check your spam folder or try resending.
                        </p>
                    </div>

                    <v-btn
                        :color="status === 'success' ? 'primary' : 'error'"
                        @click="router.push({ path: '/', query: { showLogin: 'true' } })"
                    >
                        Return to Login
                    </v-btn>
                </div>

                <!-- Notification for resend status -->
                <v-snackbar
                    v-model="showNotification"
                    :color="notificationColor"
                    :timeout="5000"
                    location="top"
                    multi-line
                >
                    {{ notificationMessage }}
                    <template v-slot:actions>
                        <v-btn
                            variant="text"
                            @click="showNotification = false"
                        >
                            Close
                        </v-btn>
                    </template>
                </v-snackbar>
            </div>
        </div>
    </main>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { Constant } from '@/config/constants';
import { UnderwaterBg } from '@/components/ui/underwaterBg';
import { useAuthStore } from '@/stores/authStore';
import type { ConfirmationResponse } from '@/interfaces/auth';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const loading = ref(true);
const status = ref<'success' | 'error'>('error');
const title = ref('');
const message = ref('');
const isExpired = ref(false);
const resending = ref(false);
const showNotification = ref(false);
const notificationColor = ref('');
const notificationMessage = ref('');
const userEmail = ref('');
const emailInput = ref('');

const resendConfirmation = async () => {
    if (resending.value) return;
    
    const emailToUse = userEmail.value || emailInput.value.trim();
    const redirectUrl = route.query.redirectUrl as string;
    
    if (!emailToUse) {
        notificationMessage.value = 'Please enter your email address.';
        notificationColor.value = 'error';
        showNotification.value = true;
        return;
    }
    
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToUse)) {
        notificationMessage.value = 'Please enter a valid email address.';
        notificationColor.value = 'error';
        showNotification.value = true;
        return;
    }
    
    try {
        resending.value = true;
        const requestData: any = { email: emailToUse };
        if (redirectUrl) {
            requestData.redirectUrl = redirectUrl;
        }
        
        await axios.post(`${Constant.API_URL}${Constant.RESEND_CONFIRMATION}`, requestData);
        notificationMessage.value = 'Confirmation email has been sent! Please check your inbox.';
        notificationColor.value = 'success';
        showNotification.value = true;
    } catch (error: any) {
        console.error('Failed to resend confirmation:', error);
        
        // Handle different error responses
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    notificationMessage.value = 'Account not found with that email address.';
                    notificationColor.value = 'error';
                    break;
                case 409:
                    notificationMessage.value = 'Your account is already verified. Please log in.';
                    notificationColor.value = 'info';
                    break;
                case 429:
                    notificationMessage.value = 'Too many attempts. Please try again later.';
                    notificationColor.value = 'warning';
                    break;
                default:
                    notificationMessage.value = error.response.data?.message || 'Failed to resend confirmation email.';
                    notificationColor.value = 'error';
            }
        } else {
            notificationMessage.value = 'Network error. Please check your connection and try again.';
            notificationColor.value = 'error';
        }
        showNotification.value = true;
    } finally {
        resending.value = false;
    }
};

onMounted(async () => {
    const confirmToken = route.query.token as string;
    const redirectUrl = route.query.redirectUrl as string;
    userEmail.value = route.query.email as string;
    
    if (!confirmToken) {
        status.value = 'error';
        title.value = 'Invalid Request';
        message.value = 'No confirmation token provided';
        loading.value = false;
        return;
    }

    try {
        const response: ConfirmationResponse = await authStore.confirmEmail(confirmToken, redirectUrl);
        
        status.value = 'success';
        title.value = 'Email Confirmed!';
        
        if (response.firstTimeConfirmation && response.loginToken) {
            // User is automatically logged in for first-time confirmation
            message.value = 'Your email has been confirmed and you are now logged in! Redirecting...';
            
            // Redirect to the provided redirectUrl or admin view after a quick delay
            setTimeout(() => {
                if (response.redirectUrl) {
                    window.location.href = response.redirectUrl;
                } else {
                    router.push('/admin');
                }
            }, 500);
        } else {
            // Email was already confirmed before
            message.value = 'Your email has been confirmed. Redirecting...';
            
            // Redirect after a quick delay
            setTimeout(() => {
                if (response.redirectUrl) {
                    window.location.href = response.redirectUrl;
                } else {
                    router.push({ path: '/', query: { showLogin: 'true' } });
                }
            }, 500);
        }
    } catch (error: any) {
        status.value = 'error';
        if (error.response?.status === 410) {
            isExpired.value = true;
            title.value = 'Link Expired';
            message.value = 'This confirmation link has expired. Would you like to receive a new one?';
        } else if (error.response?.status === 404) {
            title.value = 'Invalid Link';
            message.value = 'This confirmation link is invalid or has already been used.';
        } else {
            title.value = 'Error';
            message.value = 'An error occurred while confirming your email. Please try again.';
        }
    } finally {
        loading.value = false;
    }
});
</script>
